'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Shield, AlertTriangle, Users, Flag, Search, Lock, CheckCircle, Eye, Download, RefreshCw, ArrowRight, FileText } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import { useRealtimeAlerts } from '@/hooks/useRealtimeAlerts';
import RealtimeFeedPanel from '@/components/ui/RealtimeFeedPanel';
import { createClient } from '@/lib/supabase/client';
import { downloadCSV, downloadPDF } from '@/lib/auditExport';

const KYCApprovalRateChart = dynamic(() => import('./KYCApprovalRateChart'), { ssr: false });
const AMLTimelineChart = dynamic(() => import('./AMLTimelineChart'), { ssr: false });
const EmailDispatchPanel = dynamic(() => import('./EmailDispatchPanel'), { ssr: false });

interface ComplianceMetrics {
  kycPending: number;
  amlAlerts: number;
  eddCases: number;
  complianceHolds: number;
  approvalRate: number;
  avgKycTurnaround: string;
  sanctionHits: number;
  strFiled: number;
}

interface KYCApplication {
  id: string;
  application_ref: string;
  applicant_name: string;
  applicant_type: string;
  status: string;
  risk_level: string;
  assignee: string | null;
  submitted_at: string;
}

interface AMLAlert {
  id: string;
  alert_ref: string;
  txn_id: string | null;
  entity_name: string;
  amount: number | null;
  rule_code: string | null;
  description: string | null;
  severity: string;
  status: string;
  assignee: string | null;
  campaign: string | null;
  created_at: string;
}

interface ComplianceHold {
  id: string;
  hold_ref: string;
  entity_name: string;
  entity_type: string;
  reason: string;
  hold_type: string;
  status: string;
  placed_by: string;
  created_at: string;
}

interface SettlementBatch {
  id: string;
  batch_ref: string;
  campaign: string;
  amount: number;
  investor_count: number;
  status: string;
  created_at: string;
}

export default function ComplianceDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'kyc-queue' | 'aml-alerts' | 'sanctions' | 'str-workflow' | 'post-settlement' | 'email-dispatch'>('overview');
  const [metrics, setMetrics] = useState<ComplianceMetrics>({ kycPending: 0, amlAlerts: 0, eddCases: 0, complianceHolds: 0, approvalRate: 0, avgKycTurnaround: '—', sanctionHits: 0, strFiled: 0 });
  const [kycQueue, setKycQueue] = useState<KYCApplication[]>([]);
  const [amlAlerts, setAmlAlerts] = useState<AMLAlert[]>([]);
  const [holds, setHolds] = useState<ComplianceHold[]>([]);
  const [settlements, setSettlements] = useState<SettlementBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { alerts, isConnected, unreadCount, markAllRead, markRead, dismissAlert } = useRealtimeAlerts({
    channels: ['compliance', 'aml', 'realtime_alerts'],
  });

  let supabase = createClient();
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  // ── Export helpers ────────────────────────────────────────────────────────
  const exportKYCCSV = () => {
    const cols = [
      { key: 'application_ref', label: 'Application Ref' },
      { key: 'applicant_name', label: 'Applicant Name' },
      { key: 'applicant_type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'risk_level', label: 'Risk Level' },
      { key: 'assignee', label: 'Assignee' },
      { key: 'submitted_at', label: 'Submitted At' },
    ];
    downloadCSV(kycQueue, cols, `kyc-queue-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportKYCPDF = () => {
    const cols = [
      { key: 'application_ref', label: 'Ref' },
      { key: 'applicant_name', label: 'Applicant' },
      { key: 'applicant_type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'risk_level', label: 'Risk' },
      { key: 'assignee', label: 'Assignee' },
      { key: 'submitted_at', label: 'Submitted' },
    ];
    downloadPDF(kycQueue, cols, 'KYC / KYB Review Queue', 'Compliance Portal — Regulatory Export');
  };

  const exportAMLCSV = () => {
    const cols = [
      { key: 'alert_ref', label: 'Alert Ref' },
      { key: 'entity_name', label: 'Entity' },
      { key: 'severity', label: 'Severity' },
      { key: 'status', label: 'Status' },
      { key: 'rule_code', label: 'Rule Code' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount' },
      { key: 'txn_id', label: 'Transaction ID' },
      { key: 'assignee', label: 'Assignee' },
      { key: 'created_at', label: 'Created At' },
    ];
    downloadCSV(amlAlerts, cols, `aml-alerts-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportAMLPDF = () => {
    const cols = [
      { key: 'alert_ref', label: 'Ref' },
      { key: 'entity_name', label: 'Entity' },
      { key: 'severity', label: 'Severity' },
      { key: 'status', label: 'Status' },
      { key: 'rule_code', label: 'Rule' },
      { key: 'amount', label: 'Amount' },
      { key: 'assignee', label: 'Assignee' },
      { key: 'created_at', label: 'Date' },
    ];
    downloadPDF(amlAlerts, cols, 'AML Alerts — Audit Extract', 'Compliance Portal — Regulatory Export');
  };

  const exportHoldsCSV = () => {
    const cols = [
      { key: 'hold_ref', label: 'Hold Ref' },
      { key: 'entity_name', label: 'Entity' },
      { key: 'entity_type', label: 'Entity Type' },
      { key: 'hold_type', label: 'Hold Type' },
      { key: 'reason', label: 'Reason' },
      { key: 'status', label: 'Status' },
      { key: 'placed_by', label: 'Placed By' },
      { key: 'created_at', label: 'Created At' },
    ];
    downloadCSV(holds, cols, `compliance-holds-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const exportHoldsPDF = () => {
    const cols = [
      { key: 'hold_ref', label: 'Ref' },
      { key: 'entity_name', label: 'Entity' },
      { key: 'hold_type', label: 'Type' },
      { key: 'reason', label: 'Reason' },
      { key: 'status', label: 'Status' },
      { key: 'placed_by', label: 'Placed By' },
      { key: 'created_at', label: 'Date' },
    ];
    downloadPDF(holds, cols, 'Compliance Holds — Audit Extract', 'Compliance Portal — Regulatory Export');
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [kycRes, amlRes, holdsRes, settlementsRes] = await Promise.all([
        supabase.from('kyc_applications').select('*').order('submitted_at', { ascending: false }).limit(20),
        supabase.from('aml_alerts').select('*').order('created_at', { ascending: false }).limit(20),
        supabase.from('compliance_holds').select('*').eq('status', 'active').order('created_at', { ascending: false }),
        supabase.from('settlement_batches').select('*').order('created_at', { ascending: false }).limit(5),
      ]);

      const kycData = kycRes.data || [];
      const amlData = amlRes.data || [];
      const holdsData = holdsRes.data || [];
      const settlementsData = settlementsRes.data || [];

      setKycQueue(kycData);
      setAmlAlerts(amlData);
      setHolds(holdsData);
      setSettlements(settlementsData);

      const kycPending = kycData.filter(k => k.status === 'pending' || k.status === 'under_review' || k.status === 'edd_required').length;
      const openAml = amlData.filter(a => a.status === 'open' || a.status === 'escalated' || a.status === 'under-review').length;
      const eddCases = kycData.filter(k => k.status === 'edd_required').length;
      const approved = kycData.filter(k => k.status === 'approved').length;
      const approvalRate = kycData.length > 0 ? Math.round((approved / kycData.length) * 100) : 0;

      setMetrics({
        kycPending,
        amlAlerts: openAml,
        eddCases,
        complianceHolds: holdsData.length,
        approvalRate,
        avgKycTurnaround: '1.8 days',
        sanctionHits: amlData.filter(a => a.rule_code?.startsWith('PEP') || a.rule_code?.startsWith('SANC')).length,
        strFiled: amlData.filter(a => a.status === 'str-pending' || a.status === 'filed').length,
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Real-time data subscriptions ──────────────────────────────────────────
  useEffect(() => {
    let supabase: any = null;
    const subs: any[] = [];
    try {
      supabase = createClient();
      const handler = () => { fetchData(); };

      subs.push(
        supabase
          .channel('compliance-rt-kyc')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'kyc_applications' }, handler)
          .subscribe(),
        supabase
          .channel('compliance-rt-aml')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'aml_alerts' }, handler)
          .subscribe(),
        supabase
          .channel('compliance-rt-holds')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'compliance_holds' }, handler)
          .subscribe(),
        supabase
          .channel('compliance-rt-settlements')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'settlement_batches' }, handler)
          .subscribe(),
      );
    } catch {
      // Supabase not configured — subscriptions skipped
    }
    return () => {
      if (supabase) subs.forEach((ch) => supabase.removeChannel(ch));
    };
  }, []);

  const criticalAml = amlAlerts.filter(a => a.severity === 'critical');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">Compliance Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">AML/CFT · KYC/KYB · Sanctions · STR/SAR · EDD · Live data</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={fetchData} className="btn-secondary text-xs gap-1.5" disabled={loading}><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh Queue</button>
          <button className="btn-primary text-xs gap-1.5"><Flag size={12} /> File STR</button>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-warning">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Critical alerts banner */}
      {criticalAml.length > 0 && (
        <div className="alert-banner alert-critical">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm">{criticalAml.length} Critical AML Alert{criticalAml.length > 1 ? 's' : ''} Require Immediate Disposition</p>
            <p className="text-xs text-muted-foreground mt-0.5">{criticalAml[0]?.entity_name} — {criticalAml[0]?.description?.slice(0, 80)}...</p>
          </div>
          <button className="flex-shrink-0 btn-ghost text-xs gap-1 text-negative" onClick={() => setActiveTab('aml-alerts')}>
            Review Now <ArrowRight size={11} />
          </button>
        </div>
      )}

      {/* Live Feed */}
      <RealtimeFeedPanel alerts={alerts} isConnected={isConnected} unreadCount={unreadCount} onMarkAllRead={markAllRead} onMarkRead={markRead} onDismiss={dismissAlert} label="Live Compliance & AML Feed" maxVisible={6} />

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto scrollbar-hide">
        {([
          { key: 'overview', label: 'Overview' },
          { key: 'kyc-queue', label: 'KYC / KYB Queue', badge: metrics.kycPending },
          { key: 'aml-alerts', label: 'AML Alerts', badge: metrics.amlAlerts },
          { key: 'sanctions', label: 'Sanctions / PEP', badge: metrics.sanctionHits },
          { key: 'str-workflow', label: 'STR / SAR', badge: metrics.strFiled },
          { key: 'post-settlement', label: 'Post-Settlement Review', badge: settlements.filter(s => s.status === 'pending_approval').length },
          { key: 'email-dispatch', label: 'Email Dispatch', badge: 3 },
        ] as const).map((tab) => (
          <button key={`comp-tab-${tab.key}`} onClick={() => setActiveTab(tab.key as any)} className={`portal-tab flex-shrink-0 ${activeTab === tab.key ? 'active' : ''}`}>
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 font-mono-data ${
                tab.key === 'aml-alerts' || tab.key === 'sanctions' ? 'bg-critical/20 text-critical' :
                tab.key === 'email-dispatch'? 'bg-critical/20 text-critical' : 'bg-warning/20 text-warning'
              }`}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground text-sm">Loading compliance data...</span>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard label="KYC / KYB Pending" value={String(metrics.kycPending)} subValue="Awaiting officer review" variant="warning" icon={Users} iconColor="text-warning" />
                <MetricCard label="Open AML Alerts" value={String(metrics.amlAlerts)} subValue={`${criticalAml.length} critical — immediate action`} variant="critical" icon={AlertTriangle} iconColor="text-critical" />
                <MetricCard label="EDD Cases Active" value={String(metrics.eddCases)} subValue="Enhanced due diligence open" variant="critical" icon={Search} iconColor="text-critical" />
                <MetricCard label="Compliance Holds" value={String(metrics.complianceHolds)} subValue="Investor accounts frozen" variant="warning" icon={Lock} iconColor="text-warning" />
                <MetricCard label="Sanctions / PEP Hits" value={String(metrics.sanctionHits)} subValue="Matches requiring review" variant="critical" icon={Shield} iconColor="text-critical" />
                <MetricCard label="KYC Approval Rate" value={`${metrics.approvalRate}%`} subValue={`Avg turnaround: ${metrics.avgKycTurnaround}`} trend={3.2} trendLabel="vs last month" variant="positive" icon={CheckCircle} iconColor="text-positive" mono />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="glass-card rounded-xl p-4">
                  <SectionHeader title="KYC Status Distribution" subtitle="All investor onboarding cases" />
                  <KYCApprovalRateChart />
                </div>
                <div className="glass-card rounded-xl p-4">
                  <SectionHeader title="AML Alert Volume — 14 Days" subtitle="Triggered vs resolved per day" />
                  <AMLTimelineChart />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'kyc-queue' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="KYC / KYB Review Queue" subtitle={`${kycQueue.length} cases · Sorted by risk`} />
                <div className="flex items-center gap-2">
                  <button onClick={exportKYCCSV} className="btn-ghost text-xs gap-1.5" title="Download CSV"><Download size={13} /> CSV</button>
                  <button onClick={exportKYCPDF} className="btn-ghost text-xs gap-1.5" title="Download PDF"><FileText size={13} /> PDF</button>
                </div>
              </div>
              {kycQueue.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No KYC applications in queue.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-3">
                      <tr>
                        {['Applicant', 'Type', 'Submitted', 'Risk', 'Stage', 'Assignee', 'Actions'].map((h) => (
                          <th key={`th-kyc-${h}`} className="table-th">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {kycQueue.map((kyc) => (
                        <tr key={kyc.id} className="data-table-row">
                          <td className="table-td">
                            <p className="font-medium text-foreground">{kyc.applicant_name}</p>
                            <p className="text-xs font-mono-data text-muted-foreground">{kyc.application_ref}</p>
                          </td>
                          <td className="table-td text-xs text-muted-foreground">{kyc.applicant_type}</td>
                          <td className="table-td font-mono-data text-xs text-muted-foreground">{new Date(kyc.submitted_at).toLocaleDateString()}</td>
                          <td className="table-td">
                            <StatusBadge variant={kyc.risk_level === 'high' ? 'high' : kyc.risk_level === 'medium' ? 'medium' : 'low'} label={kyc.risk_level.toUpperCase()} />
                          </td>
                          <td className="table-td text-xs text-muted-foreground">{kyc.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                          <td className="table-td text-xs text-muted-foreground">{kyc.assignee || 'Unassigned'}</td>
                          <td className="table-td">
                            <button className="btn-ghost p-1" title="Review"><Eye size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'aml-alerts' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="AML Alerts" subtitle={`${amlAlerts.length} alerts · ${criticalAml.length} critical`} />
                <div className="flex items-center gap-2">
                  <button onClick={exportAMLCSV} className="btn-ghost text-xs gap-1.5" title="Download CSV"><Download size={13} /> CSV</button>
                  <button onClick={exportAMLPDF} className="btn-ghost text-xs gap-1.5" title="Download PDF"><FileText size={13} /> PDF</button>
                </div>
              </div>
              {amlAlerts.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No AML alerts found.</div>
              ) : (
                <div className="divide-y divide-border">
                  {amlAlerts.map((alert) => (
                    <div key={alert.id} className={`px-4 py-3 hover:bg-surface-3/40 transition-colors ${alert.severity === 'critical' ? 'border-l-2 border-l-critical' : alert.severity === 'high' ? 'border-l-2 border-l-negative' : ''}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-mono-data text-xs text-accent">{alert.alert_ref}</p>
                            <StatusBadge variant={alert.severity === 'critical' ? 'critical' : alert.severity === 'high' ? 'high' : 'medium'} label={alert.severity.toUpperCase()} />
                            <StatusBadge variant={alert.status === 'open' ? 'warning' : alert.status === 'escalated' ? 'critical' : 'review'} label={alert.status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} dot />
                          </div>
                          <p className="text-sm font-semibold text-foreground">{alert.entity_name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{alert.description}</p>
                          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground mt-1">
                            {alert.txn_id && <span>TXN: <span className="font-mono-data">{alert.txn_id}</span></span>}
                            {alert.amount && <span>Amount: <span className="font-mono-data font-bold text-foreground">{fmt(alert.amount)}</span></span>}
                            {alert.rule_code && <span>Rule: <span className="font-mono-data">{alert.rule_code}</span></span>}
                            <span>Assignee: {alert.assignee || 'Unassigned'}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button className="btn-ghost text-xs gap-1"><Eye size={12} /> Review</button>
                          <button className="btn-ghost text-xs gap-1 text-negative"><Flag size={12} /> Escalate</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'sanctions' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Compliance Holds" subtitle={`${holds.length} active holds`} />
                <div className="flex items-center gap-2">
                  <button onClick={exportHoldsCSV} className="btn-ghost text-xs gap-1.5" title="Download CSV"><Download size={13} /> CSV</button>
                  <button onClick={exportHoldsPDF} className="btn-ghost text-xs gap-1.5" title="Download PDF"><FileText size={13} /> PDF</button>
                </div>
              </div>
              {holds.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No active compliance holds.</div>
              ) : (
                <div className="divide-y divide-border">
                  {holds.map((hold) => (
                    <div key={hold.id} className="px-4 py-3 hover:bg-surface-3/40 transition-colors">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-foreground">{hold.entity_name}</p>
                            <StatusBadge variant="critical" label={hold.hold_type.toUpperCase()} />
                          </div>
                          <p className="text-xs text-muted-foreground">{hold.reason}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Ref: <span className="font-mono-data">{hold.hold_ref}</span> · Placed by: {hold.placed_by}</p>
                        </div>
                        <button className="btn-ghost text-xs gap-1"><Eye size={12} /> Review</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'str-workflow' && (
            <div className="glass-card rounded-xl p-8 text-center">
              <FileText size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground">STR / SAR Workflow</p>
              <p className="text-xs text-muted-foreground mt-1">{metrics.strFiled} cases filed. Use the File STR button to create a new report.</p>
            </div>
          )}

          {activeTab === 'post-settlement' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <SectionHeader title="Settlement Batches — Post-Settlement Review" subtitle={`${settlements.length} batches`} />
              </div>
              {settlements.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No settlement batches found.</div>
              ) : (
                <div className="divide-y divide-border">
                  {settlements.map((batch) => (
                    <div key={batch.id} className="px-4 py-3 hover:bg-surface-3/40 transition-colors">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-mono-data text-xs text-accent">{batch.batch_ref}</p>
                            <StatusBadge variant={batch.status === 'completed' ? 'confirmed' : batch.status === 'pending_approval' ? 'pending' : 'review'} label={batch.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} dot />
                          </div>
                          <p className="text-sm font-semibold text-foreground">{batch.campaign}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Amount: <span className="font-mono-data font-bold text-foreground">{fmt(batch.amount)}</span> · {batch.investor_count} investors</p>
                        </div>
                        <button className="btn-ghost text-xs gap-1"><Eye size={12} /> Review</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'email-dispatch' && <EmailDispatchPanel />}
        </>
      )}
    </div>
  );
}